import { useCallback, useState } from "react";
import { useWSEvent } from "../context/WebSocketContext";
import { describeEvent } from "../lib/notificationsMap";

const STORE_KEY = "bansal_notifications_v5";
const CAP = 40;

function readStore() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY));
    return Array.isArray(raw) ? raw.slice(0, CAP) : [];
  } catch (_) {
    return [];
  }
}

function writeStore(items) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(items.slice(0, CAP)));
  } catch (_) {
    /* ignore quota errors */
  }
}

/**
 * Accumulates live WebSocket broadcast events into an in-app notification inbox.
 * There is no server-side inbox API — this is the real-time event stream, kept
 * client-side (capped + persisted to localStorage so it survives reloads).
 */
export function useNotificationsFeed() {
  const [items, setItems] = useState(readStore);

  useWSEvent("*", (event) => {
    const described = describeEvent(event);
    if (!described) return;
    setItems((prev) => {
      const entry = {
        ...described,
        read: false,
        at: Date.now(),
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      const next = [entry, ...prev].slice(0, CAP);
      writeStore(next);
      return next;
    });
  });

  const unread = items.reduce((n, i) => (i.read ? n : n + 1), 0);

  const markAllRead = useCallback(() => {
    setItems((prev) => {
      const next = prev.map((i) => (i.read ? i : { ...i, read: true }));
      writeStore(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    writeStore([]);
  }, []);

  return { items, unread, markAllRead, clearAll };
}
